import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useAuthStore,
  selectUser,
  logout,
  getMe,
  changeMyPassword,
  isApiError,
} from "@safers/auth";
import { ChevronDown, ExternalLink, Lock } from "@safers/ui/atoms";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@safers/ui/molecules";
import {
  PasswordChangeCard,
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  ModalBody,
} from "@safers/ui/organisms";
import { useToastStore } from "@/stores";

const isPasswordChangeRejected = (err: unknown) => isApiError(err) && !err.isServerError;
const PW_MIN_LENGTH = 8;

type PasswordChangeErrors = Partial<Record<"currentPassword" | "newPassword" | "confirmPassword", string>>;

function validatePassword(
  currentPassword: string,
  newPassword: string,
  confirmPassword: string
): PasswordChangeErrors {
  const next: PasswordChangeErrors = {};
  if (!currentPassword) next.currentPassword = "필수입니다.";
  if (!newPassword) next.newPassword = "필수입니다.";
  else if (newPassword.length < PW_MIN_LENGTH)
    next.newPassword = `${PW_MIN_LENGTH}자 이상 입력하세요.`;
  if (!confirmPassword) next.confirmPassword = "필수입니다.";
  else if (newPassword !== confirmPassword)
    next.confirmPassword = "비밀번호가 일치하지 않습니다.";
  return next;
}

export function HeaderUserInfo({ tone = "light" }: { tone?: "light" | "dark" }) {
  const navigate = useNavigate();
  const user = useAuthStore(selectUser);
  const [pwModalOpen, setPwModalOpen] = useState(false);
  const [pwSubmitting, setPwSubmitting] = useState(false);
  const [pwPasswords, setPwPasswords] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [pwErrors, setPwErrors] = useState<PasswordChangeErrors>({});

  if (!user) return null;

  const dark = tone === "dark";

  const authority = user.authority ?? "USER";
  const isAdmin = authority === "ADMIN";
  // "관리자 페이지" 항목은 ADMIN 이면 어느 페이지(홈/현장/CCTV 등)에서나 공통 노출.
  const showAdminEntry = isAdmin;

  const initials = (user.name ?? user.username ?? "?").slice(0, 2).toUpperCase();

  async function handleLogout() {
    try {
      await logout();
    } catch {
      // 서버 실패해도 클라이언트 세션은 정리
    } finally {
      useAuthStore.getState().reset();
      navigate("/login", { replace: true });
    }
  }

  function handlePwModalOpenChange(next: boolean) {
    setPwModalOpen(next);
    if (!next) {
      setPwErrors({});
      setPwPasswords({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    }
  }

  function clearFieldError(field: "currentPassword" | "newPassword" | "confirmPassword") {
    setPwErrors(prev => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }

  async function handlePasswordSubmit(
    currentPassword: string,
    newPassword: string,
    confirmPassword: string
  ) {
    const next = validatePassword(currentPassword, newPassword, confirmPassword);
    setPwErrors(next);
    if (Object.keys(next).length > 0) return;

    setPwSubmitting(true);
    try {
      await changeMyPassword({
        currentPassword,
        newPassword,
      });
      useToastStore.getState().toast.success("비밀번호가 변경되었습니다.");
      getMe()
        .then((me) => useAuthStore.getState().setUser(me))
        .catch(() => {});
      handlePwModalOpenChange(false);
    } catch (err) {
      if (isPasswordChangeRejected(err)) {
        setPwErrors({
          currentPassword: "현재 비밀번호가 올바르지 않습니다."
        });
      } else {
        useToastStore.getState().toast.error("비밀번호 변경에 실패했습니다.");
      }
    } finally {
      setPwSubmitting(false);
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={`flex items-center gap-2.5 rounded-md px-2 py-1 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand ${
              dark ? "hover:bg-white/10" : "hover:bg-neutral-100"
            }`}
          >
            <span
              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold"
              style={{
                background: "var(--brand-primary-soft)",
                color: "var(--brand-primary-on-soft)",
              }}
            >
              {initials}
            </span>
            <span className="flex flex-col items-start leading-tight">
              <span className={`text-[13px] font-medium ${dark ? "text-white" : "text-primary"}`}>
                {user.name ?? user.username}
              </span>
              <span className={`text-[11px] uppercase ${dark ? "text-white/60" : "text-tertiary"}`}>
                {authority}
              </span>
            </span>
            <ChevronDown size="sm" className={dark ? "text-white/60" : "text-tertiary"} />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" sideOffset={6} className="min-w-[220px] p-0">
          {/* header */}
          <div className="border-b border-[var(--border-subtle)] px-3 py-2.5">
            <div className="text-[13px] font-semibold text-primary">{user.name ?? "—"}</div>
            <div className="font-mono text-[11px] text-tertiary">{user.username}</div>
          </div>

          {/* items */}
          <div className="p-1">
            {showAdminEntry && (
              <>
                <DropdownMenuItem asChild>
                  {/* admin 은 다른 basename(/admin)이라 SPA Link 대신 anchor — hard navigation.
                    trailing slash 로 디렉토리 301 리다이렉트 한 번을 생략 */}
                  <a
                    href="/admin/"
                    className="flex items-center gap-2 px-2 py-1.5 text-[13px] font-semibold text-brand"
                  >
                    <ExternalLink size="sm" />
                    관리자 페이지
                  </a>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem
              onClick={() => setPwModalOpen(true)}
              className="flex items-center gap-2 px-2 py-1.5 text-[13px]"
            >
              <Lock size="sm" />
              비밀번호 변경
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={handleLogout}
              className="px-2 py-1.5 text-[13px] text-error-brand"
            >
              로그아웃
            </DropdownMenuItem>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      <Modal open={pwModalOpen} onOpenChange={handlePwModalOpenChange}>
        <ModalContent className="max-w-md">
          <ModalHeader>
            <ModalTitle>비밀번호 변경</ModalTitle>
            <ModalDescription>현재 비밀번호 확인 후 새 비밀번호로 변경합니다.</ModalDescription>
          </ModalHeader>
          <ModalBody className="pt-4">
            <PasswordChangeCard
              values={pwPasswords}
              onChange={(field, value) => {
                setPwPasswords(prev => ({ ...prev, [field]: value }));
                clearFieldError(field);
              }}
              onSubmit={handlePasswordSubmit}
              isLoading={pwSubmitting}
              errors={pwErrors}
              newPasswordPlaceholder={`${PW_MIN_LENGTH}자 이상 입력`}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}
